/**
 * Display formatting for money that is stored as integer grosze (minor PLN
 * units) everywhere in the domain. The database and `@repo/core` never carry a
 * float; this is the single seam where minor units become a human string, so a
 * price is rounded for the eye in exactly one place.
 *
 * Whole złoty render without decimals ("935 zł"); a fractional amount keeps two
 * ("935,50 zł"). `Intl` supplies the non-breaking-space grouping and the "zł"
 * suffix, so the output matches the rest of the pl-PL UI.
 */
export function formatPlnMinor(minor: number): string {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    minimumFractionDigits: minor % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minor / 100)
}

/**
 * The same amount as a headline figure — whole złoty, no grosze.
 *
 * For a KPI tile and nothing else. "36 726,39 zł" in a 2rem bold face is wider
 * than the tile that holds it, and the two digits it overflows for are false
 * precision anyway: nobody reads a quarter's revenue to the grosz. A price, an
 * invoice line and a basket total all keep {@link formatPlnMinor}, where those
 * digits are the point.
 */
export function formatPlnMinorRounded(minor: number): string {
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    maximumFractionDigits: 0,
  }).format(minor / 100)
}

/**
 * The inverse seam for a form: a user types złoty, we store grosze. Accepts a
 * plain decimal in either separator (`149`, `149,50`, `149.50`) with optional
 * spaces; returns integer grosze. Anything else — a negative sign, a currency
 * suffix, more than two decimals, junk — is `null`, which the caller treats as
 * "not a price" and refuses to submit. Strict on purpose: the panel writes real
 * catalogue prices, so a fat-fingered value must be rejected, not coerced.
 */
export function parseZlotyToMinor(raw: string): number | null {
  const s = raw.trim().replace(/\s/g, '').replace(',', '.')
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null
  const [zloty = '', grosze = ''] = s.split('.')
  const minor = Number(zloty) * 100 + Number(grosze.padEnd(2, '0'))
  return Number.isSafeInteger(minor) ? minor : null
}

/**
 * Net grosze → gross grosze at a VAT rate in basis points (2300 = 23%).
 *
 * Grosz-exact on purpose, unlike the order engine's `whole-zloty` policy: the
 * panel is showing what a stored NET price actually amounts to, so rounding it
 * to whole złoty here would display a figure the invoice never uses.
 */
export function grossFromNetMinor(netMinor: number, taxRateBps: number): number {
  return Math.round(netMinor * (1 + taxRateBps / 10_000))
}

/**
 * The inverse, for an admin who knows the gross price and not the net one.
 *
 * Lossy by nature: many gross amounts map to the same net, so typing a gross
 * figure and reading the gross back can shift it by a grosz. Net is what is
 * stored, so the form re-derives the gross from the net it just computed —
 * showing the figure that will really be charged rather than the one typed.
 */
export function netFromGrossMinor(grossMinor: number, taxRateBps: number): number {
  return Math.round((grossMinor * 10_000) / (10_000 + taxRateBps))
}

/**
 * Grosze → the bare decimal string a złoty input field shows (no currency
 * suffix, comma separator to match pl-PL typing): `14900 → "149"`,
 * `14950 → "149,50"`. `null` (a quote-only row) renders as an empty field.
 */
export function formatMinorToInput(minor: number | null): string {
  if (minor === null) return ''
  const zloty = Math.floor(minor / 100)
  const grosze = minor % 100
  return grosze === 0 ? String(zloty) : `${zloty},${String(grosze).padStart(2, '0')}`
}

/**
 * Round a złoty float to the grosz.
 *
 * The storefront works in złoty floats, so "to the grosz" is two decimal
 * places — and the `+ Number.EPSILON` is not superstition: `1.005` comes out of
 * a float multiplication as `1.00499999…` and `Math.round` would take it down,
 * quoting a grosz less than the engine's integer arithmetic charges.
 *
 * Moved here from `lib/ladders.ts`, which was deleted with the discount
 * ladders. It was never about discounts — VAT is the only caller left.
 */
export function roundZloty(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100) / 100
}

/**
 * Split a formatted price into the figure and its currency.
 *
 * So the offer pages can set the FIGURE in the wide display face and leave the
 * currency in the body font at half the size — the hierarchy a price is
 * normally given, and the same one `plan.suffix` ("/ min") already sits in.
 *
 * It started as a workaround and outlived it. The first face tried here was
 * StretchPro, which has 335 glyphs and `ó` as the only Polish one, so `299 zł`
 * set whole in it handed the `ł` to a fallback: two typefaces inside one word.
 * The way out looked like printing `PLN`, which StretchPro can set — and `PLN`
 * is not wrong, it is the ISO 4217 code and what a bank statement uses. But it
 * is not what a Polish buyer reads on a price, and every other number on this
 * site says `zł` (`formatPLN`, `formatPlnMinor`, the basket, the checkout);
 * changing one screen's currency to suit one font would have left the offer
 * page disagreeing with the total it leads to.
 *
 * The face changed instead, twice, and both replacements cover Latin Extended —
 * the figure is set in Outfit now — so the split is a choice rather than a
 * constraint. It stays because it was the right setting anyway: a currency at
 * half the figure's size is how a price is normally given.
 *
 * Handles both shapes the app produces: `formatPLN`'s plain space and `Intl`'s
 * non-breaking one — `\s` covers U+00A0, so no separate class is needed — and
 * the greedy head leaves pl-PL's own NBSP thousands grouping inside the figure
 * (`1 299 zł` → `1 299` + `zł`) rather than splitting on it. Anything that does
 * not end in a currency — "Wycena indywidualna" — comes back whole with
 * `currency: null`, so a caller can never lose half a sentence by splitting it.
 */
export function splitPrice(price: string): { amount: string; currency: string | null } {
  const match = /^(.*\S)\s+(zł|PLN)$/u.exec(price)
  if (!match?.[1] || !match[2]) return { amount: price, currency: null }
  return { amount: match[1], currency: match[2] }
}
